import { isWithinInterval } from "date-fns";
import { create } from "zustand";

import type { PortalUser } from "@/app/models";
import { PORTAL_USER_TYPE } from "@/app/models";
import type { Subscription } from "@/features/billing/models";
import { isInactiveSubscription } from "@/features/consultations/utils";
import { CATEGORY_SLUG } from "@/shared/domains";

interface PortalUserStoreState {
  portalUser: PortalUser | null;
  canAccessActiveWLFeatures: boolean;
  canAccessActiveWLFeaturesWithIntakeFormCompleted: boolean;
  canAccessNutritionalCounseling: boolean;
  canAccessCoachingSessions: boolean;
  hasActiveSubscription: boolean;
  needsEmbeddablesInitialSync?: boolean;
  subscriptionsWithPendingIntakeForm?: Subscription[];
  intakeSubscriptions?: Subscription[];
  isBlackFridayActive: boolean;

  setPortalUser: (portalUser: PortalUser) => void;
  setNeedsEmbeddablesInitialSync: (
    needsEmbeddablesInitialSync: boolean,
  ) => void;
}

export const usePortalUserStore = create<PortalUserStoreState>()((set) => ({
  portalUser: null,
  canAccessActiveWLFeatures: false,
  canAccessActiveWLFeaturesWithIntakeFormCompleted: false,
  canAccessNutritionalCounseling: false,
  canAccessCoachingSessions: false,
  hasActiveSubscription: false,
  needsEmbeddablesInitialSync: false,
  isBlackFridayActive: false,

  setPortalUser: (portalUser) => {
    const hasActiveSubscription = Boolean(
      portalUser.subscriptions?.some(
        ({ status }) => !isInactiveSubscription(status),
      ),
    );

    const canAccessActiveWLFeatures = Boolean(
      portalUser.subscriptions?.some(
        ({ product, status }) =>
          (product.category.slug === CATEGORY_SLUG.WEIGHT_LOSS ||
            product.category.slug === CATEGORY_SLUG.MICRODOSING) &&
          !isInactiveSubscription(status),
      ),
    );

    const canAccessActiveWLFeaturesWithIntakeFormCompleted =
      canAccessActiveWLFeatures &&
      portalUser.subscriptions.some(
        (subscription) =>
          (subscription.product.category.slug === CATEGORY_SLUG.WEIGHT_LOSS ||
            subscription.product.category.slug === CATEGORY_SLUG.MICRODOSING) &&
          subscription.intakeFormCompleted,
      );

    const canAccessNutritionalCounseling =
      !portalUser.gym &&
      portalUser.subscriptions?.some(
        ({ product, status }) =>
          product.category.slug === CATEGORY_SLUG.WEIGHT_LOSS &&
          !isInactiveSubscription(status),
      );

    const canAccessCoachingSessions =
      canAccessActiveWLFeatures && !portalUser.gym;

    const needsEmbeddablesInitialSync = Boolean(
      portalUser.embeddables?.needsInitialSync &&
        portalUser.subscriptions.some((sub) => sub.intakeFormCompleted),
    );

    const subscriptionsWithPendingIntakeForm = portalUser.subscriptions.filter(
      ({ intakeFormCompleted, status }) =>
        !intakeFormCompleted && !isInactiveSubscription(status),
    );

    const intakeSubscriptions = portalUser.subscriptions.filter(
      ({ intakeFormCompleted, status, product }) =>
        (!intakeFormCompleted && !isInactiveSubscription(status)) ||
        product.category.slug === CATEGORY_SLUG.TESTOSTERONE,
    );

    const isBlackFridayActive = Boolean(
      portalUser.blackFriday &&
        isWithinInterval(new Date(), {
          start: new Date(portalUser.blackFriday.startsAt),
          end: new Date(portalUser.blackFriday.endsAt),
        }),
    );

    set({
      canAccessActiveWLFeatures,
      canAccessActiveWLFeaturesWithIntakeFormCompleted,
      canAccessNutritionalCounseling,
      canAccessCoachingSessions,
      hasActiveSubscription,
      needsEmbeddablesInitialSync,
      subscriptionsWithPendingIntakeForm,
      intakeSubscriptions,
      isBlackFridayActive,
    });

    if (portalUser.type) {
      set({ portalUser });
      return;
    }

    let type: PortalUser["type"] = null;

    if (portalUser.patientId) {
      type = PORTAL_USER_TYPE.PATIENT;
    }

    if (!portalUser.patientId && portalUser.embeddables) {
      type = PORTAL_USER_TYPE.COACHING;
    }

    set({ portalUser: { ...portalUser, type } });
  },

  setNeedsEmbeddablesInitialSync: (needsEmbeddablesInitialSync: boolean) =>
    set({ needsEmbeddablesInitialSync }),
}));
